import React from "react";
import { cn } from "@/lib/utils";

/* ============================================================
 * Strato Lab 3D — Technical visual system
 * All visuals are designed SVG/CSS art: isometric 3D renders,
 * blueprint line-art and FDM textures. No photography.
 * ============================================================ */

const ACCENT = "#0066cc";
const ACCENT_DARK = "#4a9be8";

const ISO_PAL = {
  light: {
    top: "#eef0f3",
    right: "#e2e6ec",
    left: "#d2d7e0",
    line: "rgba(28,31,36,0.15)",
    stroke: "#c4cad4",
  },
  dark: {
    top: "#1c212b",
    right: "#151a24",
    left: "#0f131b",
    line: "rgba(255,255,255,0.08)",
    stroke: "rgba(255,255,255,0.14)",
  },
};

/* isometric projection helper */
const K = 0.866;
const P = (x, y, z, o) => [o.x + (x - y) * K, o.y + (x + y) * 0.5 - z];
const poly = (arr) => arr.map((p) => p.join(",")).join(" ");

/* ------------------------------------------------------------
 * LayeredBlock — signature isometric FDM-printed block (3D render)
 * ------------------------------------------------------------ */
export function LayeredBlock({ tone = "light", className, float = false }) {
  const pal = ISO_PAL[tone];
  const acc = tone === "dark" ? ACCENT_DARK : ACCENT;
  const o = { x: 170, y: 150 };
  const a = 60;
  const h = 92;

  const tA = P(-a, -a, h, o);
  const tB = P(a, -a, h, o);
  const tC = P(a, a, h, o);
  const tD = P(-a, a, h, o);
  const rB0 = P(a, -a, 0, o);
  const rC0 = P(a, a, 0, o);
  const lD0 = P(-a, a, 0, o);

  const N = 18;
  const layers = [];
  for (let i = 1; i < N; i++) {
    const z = h * (1 - i / N);
    layers.push({
      key: i,
      r: [P(a, -a, z, o), P(a, a, z, o)],
      l: [P(a, a, z, o), P(-a, a, z, o)],
      accent: i === 5 || i === 11,
    });
  }

  return (
    <svg
      viewBox="0 0 340 300"
      className={cn("h-full w-full", float && "strato-float", className)}
      role="img"
      aria-label="Rappresentazione isometrica di un pezzo stampato in FDM"
    >
      {/* soft contact shadow */}
      <ellipse
        cx={o.x}
        cy={262}
        rx={116}
        ry={26}
        fill={tone === "dark" ? "rgba(0,0,0,0.5)" : "rgba(28,31,36,0.10)"}
      />
      {/* left face */}
      <polygon points={poly([tD, tC, rC0, lD0])} fill={pal.left} />
      {/* right face */}
      <polygon points={poly([tB, tC, rC0, rB0])} fill={pal.right} />
      {/* layer lines */}
      <g strokeWidth="1">
        {layers.map((ly) => (
          <g key={ly.key} stroke={ly.accent ? acc : pal.line}>
            <line x1={ly.r[0][0]} y1={ly.r[0][1]} x2={ly.r[1][0]} y2={ly.r[1][1]} />
            <line x1={ly.l[0][0]} y1={ly.l[0][1]} x2={ly.l[1][0]} y2={ly.l[1][1]} />
          </g>
        ))}
      </g>
      {/* top face */}
      <polygon
        points={poly([tA, tB, tC, tD])}
        fill={pal.top}
        stroke={pal.stroke}
        strokeWidth="1"
      />
      {/* outline */}
      <polygon
        points={poly([tA, tB, tC, tD])}
        fill="none"
        stroke={pal.stroke}
        strokeWidth="1.2"
      />
      <polyline
        points={poly([tB, rB0, rC0, lD0])}
        fill="none"
        stroke={pal.stroke}
        strokeWidth="1.2"
      />
      {/* highlighted front vertical edge */}
      <line
        x1={tC[0]}
        y1={tC[1]}
        x2={rC0[0]}
        y2={rC0[1]}
        stroke={acc}
        strokeWidth="2"
      />
    </svg>
  );
}

/* ------------------------------------------------------------
 * Wireframe grid backdrop for hero depth
 * ------------------------------------------------------------ */
export function WireBackdrop({ tone = "light", className }) {
  const pal = ISO_PAL[tone];
  const o = { x: 170, y: 210 };
  const lines = [];
  for (let i = -3; i <= 3; i++) {
    lines.push([P(i * 34, -110, 0, o), P(i * 34, 110, 0, o)]);
    lines.push([P(-110, i * 34, 0, o), P(110, i * 34, 0, o)]);
  }
  return (
    <svg viewBox="0 0 340 300" className={cn("h-full w-full", className)} aria-hidden>
      <g stroke={pal.line} strokeWidth="1">
        {lines.map((l, i) => (
          <line key={i} x1={l[0][0]} y1={l[0][1]} x2={l[1][0]} y2={l[1][1]} />
        ))}
      </g>
    </svg>
  );
}

/* ------------------------------------------------------------
 * MaterialBlock — same isometric object rendered in a PLA color.
 * Focus on material, surface shading and FDM layer lines.
 * ------------------------------------------------------------ */
function shadeHex(hex, amt) {
  let c = hex.replace("#", "");
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  const n = parseInt(c, 16);
  let r = (n >> 16) & 255;
  let g = (n >> 8) & 255;
  let b = n & 255;
  const t = amt < 0 ? 0 : 255;
  const p = Math.abs(amt);
  r = Math.round((t - r) * p) + r;
  g = Math.round((t - g) * p) + g;
  b = Math.round((t - b) * p) + b;
  return `rgb(${r},${g},${b})`;
}
function lumHex(hex) {
  let c = hex.replace("#", "");
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  const n = parseInt(c, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

export function MaterialBlock({ hex = "#F5F5F5", className, float = false }) {
  const o = { x: 170, y: 168 };
  const a = 64;
  const h = 96;
  const top = shadeHex(hex, 0.16);
  const right = shadeHex(hex, -0.04);
  const left = shadeHex(hex, -0.16);
  const light = lumHex(hex) > 0.5;
  const line = light ? "rgba(0,0,0,0.11)" : "rgba(255,255,255,0.14)";
  const rim = light ? "rgba(0,0,0,0.20)" : "rgba(255,255,255,0.26)";

  const tA = P(-a, -a, h, o);
  const tB = P(a, -a, h, o);
  const tC = P(a, a, h, o);
  const tD = P(-a, a, h, o);
  const rB0 = P(a, -a, 0, o);
  const rC0 = P(a, a, 0, o);
  const lD0 = P(-a, a, 0, o);

  const N = 24;
  const layers = [];
  for (let i = 1; i < N; i++) {
    const z = h * (1 - i / N);
    layers.push({
      key: i,
      r: [P(a, -a, z, o), P(a, a, z, o)],
      l: [P(a, a, z, o), P(-a, a, z, o)],
    });
  }

  return (
    <svg
      viewBox="0 0 340 300"
      className={cn("h-full w-full", float && "strato-float", className)}
      role="img"
      aria-label="Oggetto 3D in PLA"
    >
      <ellipse cx={o.x} cy={272} rx={108} ry={22} fill="rgba(0,0,0,0.28)" />
      <polygon points={poly([tD, tC, rC0, lD0])} fill={left} />
      <polygon points={poly([tB, tC, rC0, rB0])} fill={right} />
      <g strokeWidth="1" stroke={line}>
        {layers.map((ly) => (
          <g key={ly.key}>
            <line x1={ly.r[0][0]} y1={ly.r[0][1]} x2={ly.r[1][0]} y2={ly.r[1][1]} />
            <line x1={ly.l[0][0]} y1={ly.l[0][1]} x2={ly.l[1][0]} y2={ly.l[1][1]} />
          </g>
        ))}
      </g>
      <polygon points={poly([tA, tB, tC, tD])} fill={top} />
      {/* soft top highlight */}
      <polygon points={poly([tA, tB, tC, tD])} fill="rgba(255,255,255,0.06)" />
      {/* outline / rim for definition on any background */}
      <polygon points={poly([tA, tB, tC, tD])} fill="none" stroke={rim} strokeWidth="1.2" />
      <polyline points={poly([tB, rB0, rC0, lD0])} fill="none" stroke={rim} strokeWidth="1.2" />
      <line x1={tC[0]} y1={tC[1]} x2={rC0[0]} y2={rC0[1]} stroke={rim} strokeWidth="1.2" />
    </svg>
  );
}

/* ------------------------------------------------------------
 * BlueprintFrame — framed technical panel (grid + corner ticks)
 * ------------------------------------------------------------ */
export function BlueprintFrame({ children, tone = "light", label, className }) {
  const dark = tone === "dark";
  const corner = dark ? "border-white/25" : "border-strato-graphite/25";
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border",
        dark
          ? "border-white/12 bg-white/[0.03]"
          : "border-strato-gray-100 bg-strato-gray-50",
        className,
      )}
    >
      <div className="strato-grid-texture absolute inset-0 opacity-70" />
      <span className={cn("pointer-events-none absolute left-3 top-3 h-2.5 w-2.5 border-l border-t", corner)} />
      <span className={cn("pointer-events-none absolute right-3 top-3 h-2.5 w-2.5 border-r border-t", corner)} />
      <span className={cn("pointer-events-none absolute bottom-3 left-3 h-2.5 w-2.5 border-b border-l", corner)} />
      <span className={cn("pointer-events-none absolute bottom-3 right-3 h-2.5 w-2.5 border-b border-r", corner)} />
      <div className="relative flex h-full w-full items-center justify-center p-6">
        {children}
      </div>
      {label ? (
        <span
          className={cn(
            "strato-micro absolute left-4 top-4",
            dark ? "text-white/40" : "text-strato-graphite/40",
          )}
        >
          {label}
        </span>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------
 * VisualSlot — neutral technical media slot for a future
 * internally-produced 3D visual. Easily swappable.
 * ------------------------------------------------------------ */
export function VisualSlot({ tone = "light", label, className }) {
  const dark = tone === "dark";
  const stroke = dark ? "rgba(255,255,255,0.32)" : "rgba(28,31,36,0.28)";
  const corner = dark ? "border-white/25" : "border-strato-graphite/20";
  const muted = dark ? "text-white/40" : "text-strato-graphite/40";
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border",
        dark ? "border-white/12 bg-white/[0.03]" : "border-strato-gray-100 bg-strato-gray-50",
        className,
      )}
    >
      <div className="strato-grid-texture absolute inset-0 opacity-50" />
      <span className={cn("pointer-events-none absolute left-3 top-3 h-2.5 w-2.5 border-l border-t", corner)} />
      <span className={cn("pointer-events-none absolute right-3 top-3 h-2.5 w-2.5 border-r border-t", corner)} />
      <span className={cn("pointer-events-none absolute bottom-3 left-3 h-2.5 w-2.5 border-b border-l", corner)} />
      <span className={cn("pointer-events-none absolute bottom-3 right-3 h-2.5 w-2.5 border-b border-r", corner)} />
      <div className="relative flex h-full w-full flex-col items-center justify-center gap-3 p-6">
        <svg width="52" height="52" viewBox="0 0 56 56" fill="none" aria-hidden>
          <path
            d="M28 6 L50 18 L50 40 L28 52 L6 40 L6 18 Z"
            stroke={stroke}
            strokeWidth="1.4"
          />
          <path
            d="M28 6 L28 30 M28 30 L6 18 M28 30 L50 18"
            stroke={stroke}
            strokeWidth="1.2"
          />
        </svg>
      </div>
      {label ? (
        <span className={cn("strato-micro absolute left-4 top-4", muted)}>{label}</span>
      ) : null}
    </div>
  );
}

/* stroke palette for line-art glyphs */
const strokes = (tone) =>
  tone === "dark"
    ? { main: "rgba(255,255,255,0.85)", dim: "rgba(255,255,255,0.3)", acc: ACCENT_DARK }
    : { main: "#1c1f24", dim: "rgba(28,31,36,0.3)", acc: ACCENT };

const GlyphSvg = ({ children, label }) => (
  <svg
    viewBox="0 0 160 140"
    className="h-full w-full"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
    role="img"
    aria-label={label}
  >
    {children}
  </svg>
);

/* ---- Application category glyphs (blueprint line-art) ---- */

export function GlyphPrototype({ tone = "light" }) {
  const s = strokes(tone);
  return (
    <GlyphSvg label="Prototipo funzionale">
      <rect x="48" y="42" width="64" height="56" rx="8" stroke={s.main} strokeWidth="1.6" />
      {[56, 68, 80].map((y) => (
        <line key={y} x1="48" y1={y} x2="112" y2={y} stroke={s.dim} strokeWidth="1.2" />
      ))}
      <path d="M48 90 L112 90" stroke={s.dim} strokeWidth="1.2" />
      {/* dimension line */}
      <path d="M36 42 L36 98" stroke={s.acc} strokeWidth="1.2" />
      <path d="M33 45 L36 42 L39 45 M33 95 L36 98 L39 95" stroke={s.acc} strokeWidth="1.2" />
      <circle cx="112" cy="42" r="3" fill={s.acc} />
    </GlyphSvg>
  );
}

export function GlyphTechComponent({ tone = "light" }) {
  const s = strokes(tone);
  const holes = [];
  const cx = 80, cy = 70, r = 30;
  for (let i = 0; i < 6; i++) {
    const ang = (Math.PI / 3) * i - Math.PI / 2;
    holes.push([cx + Math.cos(ang) * 20, cy + Math.sin(ang) * 20]);
  }
  return (
    <GlyphSvg label="Componente tecnico">
      <circle cx={cx} cy={cy} r={r} stroke={s.main} strokeWidth="1.6" />
      <circle cx={cx} cy={cy} r="9" stroke={s.main} strokeWidth="1.6" />
      {holes.map((h, i) => (
        <circle key={i} cx={h[0]} cy={h[1]} r="2.4" fill={i === 0 ? s.acc : s.dim} />
      ))}
      <line x1={cx + r} y1={cy} x2={cx + r + 12} y2={cy} stroke={s.acc} strokeWidth="1.2" />
    </GlyphSvg>
  );
}

export function GlyphSparePart({ tone = "light" }) {
  const s = strokes(tone);
  return (
    <GlyphSvg label="Ricambio e adattamento">
      <path
        d="M44 58 L84 50 L118 62 L114 92 L70 98 L46 86 Z"
        stroke={s.main}
        strokeWidth="1.6"
      />
      <circle cx="66" cy="72" r="6" stroke={s.main} strokeWidth="1.4" />
      <circle cx="100" cy="74" r="6" stroke={s.acc} strokeWidth="1.4" />
      <path d="M84 50 L84 98" stroke={s.dim} strokeWidth="1.1" strokeDasharray="3 4" />
    </GlyphSvg>
  );
}

export function GlyphBracket({ tone = "light" }) {
  const s = strokes(tone);
  return (
    <GlyphSvg label="Supporto e staffa">
      <path d="M52 40 L72 40 L72 82 L112 82 L112 100 L52 100 Z" stroke={s.main} strokeWidth="1.6" />
      <path d="M72 82 L52 82" stroke={s.dim} strokeWidth="1.1" />
      <circle cx="62" cy="52" r="3.4" stroke={s.main} strokeWidth="1.3" />
      <circle cx="98" cy="91" r="3.4" stroke={s.acc} strokeWidth="1.3" />
    </GlyphSvg>
  );
}

export function GlyphEnclosure({ tone = "light" }) {
  const s = strokes(tone);
  return (
    <GlyphSvg label="Contenitore e guscio">
      {/* isometric open box */}
      <polygon points="52,66 80,52 118,68 90,82" stroke={s.main} strokeWidth="1.5" />
      <path d="M52 66 L52 96 L90 112 L90 82" stroke={s.main} strokeWidth="1.5" />
      <path d="M90 82 L90 112 L118 98 L118 68" stroke={s.main} strokeWidth="1.5" />
      <polygon points="60,68 80,60 104,70 86,79" stroke={s.acc} strokeWidth="1.2" />
    </GlyphSvg>
  );
}

export function GlyphSeries({ tone = "light" }) {
  const s = strokes(tone);
  const block = (x, acc) => (
    <g key={x}>
      <rect x={x} y="56" width="26" height="34" rx="4" stroke={acc ? s.acc : s.main} strokeWidth="1.5" />
      <line x1={x} y1="66" x2={x + 26} y2="66" stroke={s.dim} strokeWidth="1" />
      <line x1={x} y1="76" x2={x + 26} y2="76" stroke={s.dim} strokeWidth="1" />
    </g>
  );
  return (
    <GlyphSvg label="Piccole serie">
      {block(40)}
      {block(72, true)}
      {block(104)}
    </GlyphSvg>
  );
}

export const APP_GLYPHS = {
  prototype: GlyphPrototype,
  techComponent: GlyphTechComponent,
  sparePart: GlyphSparePart,
  bracket: GlyphBracket,
  enclosure: GlyphEnclosure,
  series: GlyphSeries,
};

/* ---- "Come produciamo" process diagrams (line-art) ---- */

const ProcSvg = ({ children, label }) => (
  <svg
    viewBox="0 0 96 96"
    className="h-full w-full"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
    role="img"
    aria-label={label}
  >
    {children}
  </svg>
);

export function DiagFilePrep({ tone = "dark" }) {
  const s = strokes(tone);
  return (
    <ProcSvg label="Preparazione file">
      <path d="M30 22 H56 L66 32 V74 H30 Z" stroke={s.main} strokeWidth="1.6" />
      <path d="M56 22 V32 H66" stroke={s.main} strokeWidth="1.6" />
      {/* triangulated mesh */}
      <path d="M36 44 L48 38 L58 46 L48 54 Z" stroke={s.acc} strokeWidth="1.3" />
      <path d="M36 44 L48 54 L38 62 M58 46 L60 60 L48 54" stroke={s.dim} strokeWidth="1.2" />
    </ProcSvg>
  );
}

export function DiagOrientation({ tone = "dark" }) {
  const s = strokes(tone);
  return (
    <ProcSvg label="Orientamento">
      <rect x="34" y="42" width="30" height="24" rx="3" stroke={s.main} strokeWidth="1.6" transform="rotate(-14 49 54)" />
      <path d="M28 40 A26 26 0 0 1 70 34" stroke={s.acc} strokeWidth="1.5" />
      <path d="M66 30 L71 34 L65 38" stroke={s.acc} strokeWidth="1.5" />
      <line x1="20" y1="72" x2="76" y2="72" stroke={s.dim} strokeWidth="1.2" />
    </ProcSvg>
  );
}

export function DiagSlicing({ tone = "dark" }) {
  const s = strokes(tone);
  return (
    <ProcSvg label="Slicing">
      {[30, 42, 54, 66].map((y, i) => (
        <g key={y}>
          <polygon
            points={`34,${y} 62,${y - 6} 74,${y} 46,${y + 6}`}
            stroke={i === 1 ? s.acc : s.main}
            strokeWidth="1.4"
          />
        </g>
      ))}
    </ProcSvg>
  );
}

export function DiagPrint({ tone = "dark" }) {
  const s = strokes(tone);
  return (
    <ProcSvg label="Stampa FDM">
      {/* nozzle */}
      <path d="M40 24 H60 V38 L52 48 H48 L40 38 Z" stroke={s.main} strokeWidth="1.6" />
      <line x1="50" y1="48" x2="50" y2="58" stroke={s.acc} strokeWidth="2" />
      {/* deposited layers */}
      {[64, 69, 74].map((y, i) => (
        <line key={y} x1="30" y1={y} x2="70" y2={y} stroke={i === 0 ? s.acc : s.dim} strokeWidth="1.6" />
      ))}
      <line x1="24" y1="79" x2="76" y2="79" stroke={s.main} strokeWidth="1.4" />
    </ProcSvg>
  );
}

export function DiagQuality({ tone = "dark" }) {
  const s = strokes(tone);
  return (
    <ProcSvg label="Controllo finale">
      {/* caliper */}
      <rect x="30" y="46" width="24" height="18" rx="2" stroke={s.main} strokeWidth="1.5" />
      <line x1="26" y1="40" x2="58" y2="40" stroke={s.main} strokeWidth="1.5" />
      <line x1="30" y1="40" x2="30" y2="46" stroke={s.main} strokeWidth="1.5" />
      <line x1="54" y1="40" x2="54" y2="46" stroke={s.main} strokeWidth="1.5" />
      {/* check */}
      <path d="M56 62 L64 70 L78 52" stroke={s.acc} strokeWidth="2.2" />
    </ProcSvg>
  );
}

export const PROC_DIAGRAMS = {
  file: DiagFilePrep,
  orientation: DiagOrientation,
  slicing: DiagSlicing,
  print: DiagPrint,
  quality: DiagQuality,
};
